import { createFileRoute } from '@tanstack/react-router';
import TablerIconHome from '~icons/tabler/home';
import TablerIconSkiJumping from '~icons/tabler/ski-jumping';

import { Fade, Stack, Typography } from 'gooey';
import { Img, ImgLogo } from '#/components/images';
import { ButtonLink } from '#/components/links';
import fx from '#/fx';
import { Content } from '#/lib/content';
import { seo } from '#/lib/head';
import { cn } from '#/utils/classnames';

export const Route = createFileRoute('/_blank/signup_/confirmation')({
  validateSearch: (search: Record<string, unknown>): { plan?: string } => {
    return {
      plan: (search.plan as SignupPlan) || 'starter',
    };
  },
  loaderDeps: ({ search }) => ({
    plan: (search.plan ?? 'starter') as SignupPlan,
  }),
  loader: async ({ deps }) => {
    return load(deps.plan);
  },
  head: () => {
    return {
      meta: seo({
        title: 'Signup Confirmation',
        description: 'Your signup has been confirmed.',
      }),
    };
  },
  component: BookingConfirmationPage,
});

async function load(plan: SignupPlan) {
  const page = await fx.getPageContentBySlug({
    data: {
      key: 'signup',
      slug: plan,
    },
  });

  return { page };
}

function BookingConfirmationPage() {
  const { page } = Route.useLoaderData();
  return (
    <div
      className={cn(
        'min-w-screen min-h-screen overflow-x-hidden py-12 bg-black',
        'flex items-center justify-center flex-col'
      )}
    >
      <Fade in>
        {(transitionProps) => (
          <div
            {...transitionProps}
            className={cn(
              'w-[90vw] max-w-[1000px] mx-auto relative z-20 text-center',
              transitionProps.className
            )}
          >
            <ImgLogo logo='contrast' className='mx-auto max-w-[200px] mb-8' />
            <Typography variant='h1' component='h1' className='text-center mb-8'>
              <Content content={page.sections.confirmation.title} />
            </Typography>
            <Typography variant='subtitle1'>
              <Content content={page.sections.confirmation.subtitle} />
            </Typography>
            {(
              [
                { className: 'sm:hidden md:hidden mt-6', size: 'medium' },
                { className: 'hidden sm:flex md:hidden mt-10', size: 'large' },
                { className: 'hidden md:flex mt-10', size: 'xlarge' },
              ] as const
            ).map((btnProps) => (
              <Stack
                key={btnProps.size}
                direction='row'
                alignItems='center'
                justifyContent='center'
                spacing={1}
              >
                <ButtonLink
                  key={btnProps.size}
                  startIcon={<TablerIconSkiJumping />}
                  to='/booking/$id'
                  params={{
                    id: page.sections.confirmation.bookingId,
                  }}
                  variant='contained'
                  color='primary'
                  className={btnProps.className}
                  size={btnProps.size}
                >
                  Skip the Line
                </ButtonLink>
                <ButtonLink
                  key={btnProps.size}
                  startIcon={<TablerIconHome />}
                  to='/'
                  variant='outlined'
                  color='primary'
                  className={btnProps.className}
                  size={btnProps.size}
                >
                  Go to Home
                </ButtonLink>
              </Stack>
            ))}
          </div>
        )}
      </Fade>
      <div className='absolute bg-black/90 inset-0 z-10' />
      <div className='absolute inset-x-4 top-2/3 -translate-y-2/3 max-h-screen max-w-[1600px] mx-auto z-0'>
        <Img
          location='static'
          src={page.sections.confirmation.image.src}
          alt='Adventure Time'
          width={page.sections.confirmation.image.width}
          height={page.sections.confirmation.image.height}
          className='bg-blend-overlay'
          responsive
        />
      </div>
    </div>
  );
}

type SignupPlan = 'starter' | 'growth' | 'scale';
